import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, Calendar, MapPin, Building2, CreditCard, Mail, CheckCircle2, Download, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ShippingLabelsModal } from "@/components/ShippingLabelsModal";

// Map canonical time slot codes to human-friendly display
const timeSlotDisplay: Record<string, string> = {
  '9-11am': '9:00 AM - 11:00 AM',
  '12-2pm': '12:00 PM - 2:00 PM',
  '3-5pm': '3:00 PM - 5:00 PM',
  '6-8pm': '6:00 PM - 8:00 PM',
};

interface ShipmentDetails {
  id: string;
  pickup_date: string;
  pickup_time_slot: string;
  box_ids: string[];
  boxes: Array<{
    id: string;
    box_number: string;
    name: string;
    items: number;
  }>;
  user: {
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    zip: string;
    full_name: string;
  };
}

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState<ShipmentDetails | null>(null);
  const [checklist, setChecklist] = useState([false, false, false]);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [labels, setLabels] = useState<any[]>([]);
  const [generatingLabels, setGeneratingLabels] = useState(false);
  const [emailingLabels, setEmailingLabels] = useState(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const fetchShipmentDetails = async () => {
      if (!sessionId) {
        toast({
          title: "Error",
          description: "No checkout session found",
          variant: "destructive",
        });
        navigate("/dashboard");
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");

        // Get the most recent shipment for this user
        const { data: shipmentData, error: shipmentError } = await supabase
          .from("shipments")
          .select(`
            id,
            pickup_date,
            pickup_time_slot,
            box_ids
          `)
          .eq("user_id", user.id)
          .eq("type", "to_storage")
          .eq("status", "scheduled")
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        if (shipmentError) throw shipmentError;

        // Get box details
        const { data: boxesData, error: boxesError } = await supabase
          .from("boxes")
          .select("id, box_number, name")
          .in("id", shipmentData.box_ids);

        if (boxesError) throw boxesError;

        // Get item counts for each box
        const boxesWithItems = await Promise.all(
          boxesData.map(async (box) => {
            const { count } = await supabase
              .from("items")
              .select("*", { count: "exact", head: true })
              .eq("box_id", box.id);
            return { ...box, items: count || 0 };
          })
        );

        // Get user profile
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("address_line1, address_line2, city, state, zip, full_name")
          .eq("id", user.id)
          .single();

        if (profileError) throw profileError;

        setShipment({
          ...shipmentData,
          boxes: boxesWithItems,
          user: profile,
        });
      } catch (error) {
        console.error("Error fetching shipment:", error);
        toast({
          title: "Error",
          description: "Failed to load shipment details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchShipmentDetails();
  }, [sessionId, navigate, toast]);

  const toggleCheckItem = (index: number) => {
    setChecklist(prev => {
      const newList = [...prev];
      newList[index] = !newList[index];
      return newList;
    });
  };

  const handleGenerateLabels = async () => {
    if (!shipment) return;

    setGeneratingLabels(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke('generate-shipping-labels', {
        body: { 
          box_ids: shipment.box_ids,
          user_id: user.id
        }
      });

      if (error) throw error;

      // Check for incomplete profile error
      if (data?.error === 'incomplete_profile') {
        const missingFieldsMap: Record<string, string> = {
          'valid_phone': 'Valid phone number (currently using placeholder)',
          'phone': 'Phone number',
          'address_line1': 'Street address',
          'city': 'City',
          'state': 'State',
          'zip': 'ZIP code',
          'full_name': 'Full name'
        };

        const missingFields = data.missing_fields || [];
        const fieldsList = missingFields
          .map((field: string) => missingFieldsMap[field] || field)
          .join('\n• ');

        toast({
          title: "Profile Incomplete",
          description: (
            <div>
              <p className="mb-2">Please update your profile with the following information:</p>
              <p className="mb-3 pl-2">• {fieldsList}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/settings')}
                className="mt-2"
              >
                Go to Settings
              </Button>
            </div>
          ),
          variant: "destructive",
          duration: 10000,
        });
        return;
      }

      if (data?.labels) {
        setLabels(data.labels);
        setShowLabelsModal(true);
        toast({
          title: "Labels Generated",
          description: `${data.labels.length} shipping labels are ready to download`,
        });
      }
    } catch (error) {
      console.error("Error generating labels:", error);
      toast({
        title: "Error",
        description: "Failed to generate shipping labels. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGeneratingLabels(false);
    }
  };

  const handleEmailLabels = async () => {
    if (!shipment) return;
    
    setEmailingLabels(true);
    try {
      const { data, error } = await supabase.functions.invoke('email-shipping-labels', {
        body: { shipmentId: shipment.id }
      });

      if (error) throw error;

      toast({
        title: "Labels Sent",
        description: "Shipping labels have been sent to your email",
      });
    } catch (error: any) {
      console.error('Error emailing labels:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to email labels. Please try again.",
        variant: "destructive",
      });
    } finally {
      setEmailingLabels(false);
    }
  };

  const totalCost = shipment?.boxes.length ? (shipment.boxes.length * 9.99).toFixed(2) : "0.00";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Shipment Not Found</CardTitle>
            <CardDescription>We couldn't find your shipment details.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold text-primary">Endless Storage</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Success Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <CheckCircle2 className="h-8 w-8 text-primary animate-scale-in" />
          </div>
          <h1 className="text-3xl font-bold mb-2">You're all set!</h1>
          <p className="text-lg text-muted-foreground">
            Your pickup is scheduled for:
          </p>
          <p className="text-2xl font-semibold text-primary mt-2">
            {format(new Date(shipment.pickup_date), "EEEE, MMMM d, yyyy")}
          </p>
          <p className="text-lg text-muted-foreground mt-1">
            {timeSlotDisplay[shipment.pickup_time_slot] || shipment.pickup_time_slot}
          </p>
        </div>

        {/* Summary Card */}
        <Card className="mb-6 shadow-elegant">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Package className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold">{shipment.boxes.length} boxes going to storage</p>
                  <p className="text-sm text-muted-foreground">
                    {shipment.boxes.reduce((sum, box) => sum + box.items, 0)} total items
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">${totalCost}</p>
                <p className="text-sm text-muted-foreground">per month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Checklist */}
        <Card className="mb-6 shadow-elegant">
          <CardHeader>
            <CardTitle>Before Your Pickup</CardTitle>
            <CardDescription>Complete these steps to ensure a smooth pickup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={checklist[0]}
                onCheckedChange={() => toggleCheckItem(0)}
                className="mt-1"
              />
              <div className="flex-1">
                <p className="font-medium">Print and attach shipping labels to your boxes</p>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleGenerateLabels}
                    disabled={generatingLabels}
                    className="flex-1 sm:flex-none"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {generatingLabels ? "Generating..." : "Download Labels (PDF)"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleEmailLabels}
                    disabled={emailingLabels}
                    className="flex-1 sm:flex-none"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {emailingLabels ? "Sending..." : "Email Me Labels"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={checklist[1]}
                onCheckedChange={() => toggleCheckItem(1)}
                className="mt-1"
              />
              <div className="flex-1">
                <p className="font-medium">Pack your boxes securely and tape them closed</p>
                <p className="text-sm text-muted-foreground">Use packing tape on all seams. No loose items.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox
                checked={checklist[2]}
                onCheckedChange={() => toggleCheckItem(2)}
                className="mt-1"
              />
              <div className="flex-1">
                <p className="font-medium">Have boxes ready in your lobby or by the door</p>
                <p className="text-sm text-muted-foreground">Driver will call 15 mins before arrival</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pickup Details */}
        <Card className="mb-6 shadow-elegant">
          <CardHeader>
            <CardTitle>Pickup Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">Date & Time</p>
                <p className="text-muted-foreground">
                  {format(new Date(shipment.pickup_date), "EEEE, MMMM d, yyyy")} • {timeSlotDisplay[shipment.pickup_time_slot] || shipment.pickup_time_slot}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">Pickup Address</p>
                <p className="text-muted-foreground">
                  {shipment.user.address_line1}
                  {shipment.user.address_line2 && <>, {shipment.user.address_line2}</>}
                  <br />
                  {shipment.user.city}, {shipment.user.state} {shipment.user.zip}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Package className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">Boxes to Ship</p>
                <div className="mt-2 space-y-2">
                  {shipment.boxes.map((box) => (
                    <div key={box.id} className="text-sm p-2 bg-muted/50 rounded">
                      {box.box_number} • {box.name} ({box.items} items)
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">Monthly Billing</p>
                <p className="text-muted-foreground">
                  ${totalCost}/month ({shipment.boxes.length} boxes × $9.99)
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  First charge will be processed after boxes arrive at our facility
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Confirmation */}
        <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg mb-6">
          <Mail className="h-5 w-5 text-primary" />
          <p className="text-sm text-muted-foreground">
            Confirmation email sent with shipping labels and pickup instructions
          </p>
        </div>

        {/* Support Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Have questions or concerns?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Mail className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Contact our support team:</p>
                <a 
                  href="mailto:support@endless-storage.com"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  support@endless-storage.com
                </a>
              </div>
            </div>
            
            <div>
              <p className="font-medium text-sm">What if I'm not home during pickup?</p>
              <p className="text-sm text-muted-foreground">
                Leave boxes in your lobby or doorway. The driver will call you when they arrive.
              </p>
            </div>
            <div>
              <p className="font-medium text-sm">Can I cancel my pickup?</p>
              <p className="text-sm text-muted-foreground">
                Yes, you can cancel up to 24 hours before your scheduled time with no charge.
              </p>
            </div>
            <div>
              <p className="font-medium text-sm">When will I be charged?</p>
              <p className="text-sm text-muted-foreground">
                Your first payment will be charged after your boxes arrive at our facility (typically 1-2 days after pickup).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <ShippingLabelsModal 
        labels={labels}
        open={showLabelsModal}
        onOpenChange={setShowLabelsModal}
      />
    </div>
  );
};

export default CheckoutSuccess;
